import { ArrowDownRight, ArrowUpRight, RefreshCw, Shuffle, History } from 'lucide-react';
import type { StockMovement } from '../types/stockMovements.types';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

interface StockMovementsTableProps {
  items: StockMovement[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: any; colorClass: string; bgColorClass: string }> = {
  stock_in: { label: 'Nhập kho', icon: ArrowDownRight, colorClass: 'text-emerald-600', bgColorClass: 'bg-emerald-50' },
  stock_out: { label: 'Xuất kho', icon: ArrowUpRight, colorClass: 'text-rose-600', bgColorClass: 'bg-rose-50' },
  adjustment: { label: 'Điều chỉnh', icon: RefreshCw, colorClass: 'text-amber-600', bgColorClass: 'bg-amber-50' },
  transfer: { label: 'Luân chuyển', icon: Shuffle, colorClass: 'text-blue-600', bgColorClass: 'bg-blue-50' },
};

export function StockMovementsTable({
  items,
  total,
  page,
  pageSize,
  isLoading,
  onPageChange,
}: StockMovementsTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isLoading) {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-slate-500">
        <History className="w-12 h-12 mb-4 text-slate-300" />
        <p className="sm:text-sm text-xs">Không có lịch sử giao dịch nào.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto min-h-0 relative">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-40">Thời gian</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Vật tư</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Loại giao dịch</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32 text-right">Số lượng</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-48">Nguồn / Đích</th>
              <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-48">Người thực hiện</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {items.map((item) => {
              const config = TYPE_CONFIG[item.transaction_type] || { label: 'Khác', icon: History, colorClass: 'text-slate-600', bgColorClass: 'bg-slate-50' };
              const Icon = config.icon;
              const sign = item.transaction_type === 'stock_out' ? '-' : item.transaction_type === 'stock_in' ? '+' : '';

              return (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                    <div>{format(new Date(item.transaction_date || item.created_at), 'dd/MM/yyyy')}</div>
                    <div className="text-[10px] text-slate-400">{format(new Date(item.created_at), 'HH:mm', { locale: vi })}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.item?.name || 'Vật tư đã xóa'}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{item.item?.code || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium ${config.bgColorClass} ${config.colorClass}`}>
                      <Icon className="w-3 h-3" />
                      {config.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className={`font-mono font-medium sm:text-sm text-xs ${config.colorClass}`}>
                      {sign}{item.quantity} {item.item?.unit}
                    </div>
                  </td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                    <div className="truncate max-w-[180px]" title={item.warehouse?.name || item.project?.name || ''}>
                      {item.warehouse?.name || item.project?.name || '-'}
                    </div>
                    {item.note && (
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[180px]" title={item.note}>
                        {item.note}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                    {item.creator?.full_name || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white">
        <div className="sm:text-sm text-xs text-slate-500">
          Hiển thị <span className="font-medium">{(page - 1) * pageSize + 1}</span> - <span className="font-medium">{Math.min(page * pageSize, total)}</span> trong số <span className="font-medium">{total}</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Trước
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-1 border border-slate-200 rounded-md sm:text-sm text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Sau
          </button>
        </div>
      </div>
    </div>
  );
}
